import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Filter as FilterIcon,
  Download,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import {
  listDiagnostics,
  options as fetchOptions,
  downloadExport,
} from "@/lib/adminApi";

const ESTADOS = [
  "AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA",
  "MG", "MS", "MT", "PA", "PB", "PE", "PI", "PR", "RJ", "RN",
  "RO", "RR", "RS", "SC", "SE", "SP", "TO",
];

const SEGMENTOS = [
  "Indústria", "Comércio / Varejo", "Serviços", "Tecnologia", "Saúde",
  "Educação", "Construção Civil", "Agronegócio", "Logística", "Financeiro",
  "Alimentação", "Outro",
];

const COLABORADORES = [
  "1 a 9", "10 a 49", "50 a 99", "100 a 249",
  "250 a 499", "500 a 999", "1000+",
];

const BADGE_COLOR = (score) => {
  if (score >= 80) return "#16A34A";
  if (score >= 60) return "#65A30D";
  if (score >= 40) return "#FACC15";
  return "#DC2626";
};

const CLASSIFY = (score) => {
  if (score >= 80) return "Excelente";
  if (score >= 60) return "Bom";
  if (score >= 40) return "Atenção";
  return "Crítico";
};

export default function AdminDiagnosticsList() {
  const [q, setQ] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    estado: "",
    cidade: "",
    segmento: "",
    colaboradores: "",
    min_score: "",
    max_score: "",
    status_comercial: "",
    sessao_estrategica: "",
    date_from: "",
    date_to: "",
  });
  const [page, setPage] = useState(1);
  const [size] = useState(20);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [opts, setOpts] = useState({ status_comercial: [] });

  useEffect(() => {
    fetchOptions().then(setOpts).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size };
      if (q.trim()) params.q = q.trim();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) params[k] = v;
      });
      const r = await listDiagnostics(params);
      setData(r);
    } finally {
      setLoading(false);
    }
  }, [q, filters, page, size]);

  useEffect(() => {
    load();
  }, [load]);

  const activeFilters = useMemo(
    () => Object.entries(filters).filter(([, v]) => v !== "").length,
    [filters]
  );

  const totalPages = Math.max(1, Math.ceil(data.total / size));

  const setF = (k, v) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [k]: v }));
  };

  const clearAll = () => {
    setPage(1);
    setFilters({
      estado: "", cidade: "", segmento: "", colaboradores: "",
      min_score: "", max_score: "", status_comercial: "",
      sessao_estrategica: "", date_from: "", date_to: "",
    });
    setQ("");
  };

  return (
    <div data-testid="admin-diagnostics-list">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="hub-eyebrow">Diagnósticos</div>
          <h1 className="mt-2 font-display font-medium text-3xl sm:text-4xl tracking-tight">
            Base completa
          </h1>
          <p className="mt-2 text-slate-600">
            Pesquise, filtre e gerencie todos os diagnósticos concluídos.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            className="hub-btn-secondary"
            onClick={() => downloadExport("csv")}
            data-testid="btn-export-csv-inline"
          >
            <Download size={14} /> CSV
          </button>
          <button
            className="hub-btn-primary"
            onClick={() => downloadExport("xlsx")}
            data-testid="btn-export-xlsx-inline"
          >
            <Download size={14} /> Excel
          </button>
        </div>
      </div>

      {/* Search + filter toggle */}
      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            data-testid="search-input"
            className="hub-input pl-11"
            placeholder="Empresa, responsável, cidade, e-mail, telefone…"
            value={q}
            onChange={(e) => {
              setPage(1);
              setQ(e.target.value);
            }}
          />
        </div>
        <button
          data-testid="btn-toggle-filters"
          className="hub-btn-secondary"
          onClick={() => setShowFilters((s) => !s)}
        >
          <FilterIcon size={14} /> Filtros
          {activeFilters > 0 && (
            <span className="ml-1 bg-[#0F172A] text-white text-[10px] rounded-full h-5 min-w-5 px-1 grid place-items-center">
              {activeFilters}
            </span>
          )}
        </button>
      </div>

      {showFilters && (
        <div className="mt-4 hub-card p-6" data-testid="filters-panel">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Estado">
              <select
                data-testid="filter-estado"
                className="hub-input"
                value={filters.estado}
                onChange={(e) => setF("estado", e.target.value)}
              >
                <option value="">Todos</option>
                {ESTADOS.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Cidade">
              <input
                data-testid="filter-cidade"
                className="hub-input"
                placeholder="Ex.: Salvador"
                value={filters.cidade}
                onChange={(e) => setF("cidade", e.target.value)}
              />
            </Field>
            <Field label="Segmento">
              <select
                data-testid="filter-segmento"
                className="hub-input"
                value={filters.segmento}
                onChange={(e) => setF("segmento", e.target.value)}
              >
                <option value="">Todos</option>
                {SEGMENTOS.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Colaboradores">
              <select
                data-testid="filter-colaboradores"
                className="hub-input"
                value={filters.colaboradores}
                onChange={(e) => setF("colaboradores", e.target.value)}
              >
                <option value="">Todos</option>
                {COLABORADORES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Índice HUB — mínimo">
              <input
                data-testid="filter-min-score"
                type="number"
                min={0}
                max={100}
                className="hub-input"
                value={filters.min_score}
                onChange={(e) => setF("min_score", e.target.value)}
              />
            </Field>
            <Field label="Índice HUB — máximo">
              <input
                data-testid="filter-max-score"
                type="number"
                min={0}
                max={100}
                className="hub-input"
                value={filters.max_score}
                onChange={(e) => setF("max_score", e.target.value)}
              />
            </Field>
            <Field label="Status Comercial">
              <select
                data-testid="filter-status"
                className="hub-input"
                value={filters.status_comercial}
                onChange={(e) => setF("status_comercial", e.target.value)}
              >
                <option value="">Todos</option>
                {(opts.status_comercial || []).map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Sessão Estratégica">
              <select
                data-testid="filter-sessao"
                className="hub-input"
                value={filters.sessao_estrategica}
                onChange={(e) => setF("sessao_estrategica", e.target.value)}
              >
                <option value="">Todas</option>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </Field>
            <Field label="Período — de">
              <input
                data-testid="filter-date-from"
                type="date"
                className="hub-input"
                value={filters.date_from}
                onChange={(e) => setF("date_from", e.target.value)}
              />
            </Field>
            <Field label="Período — até">
              <input
                data-testid="filter-date-to"
                type="date"
                className="hub-input"
                value={filters.date_to}
                onChange={(e) => setF("date_to", e.target.value)}
              />
            </Field>
          </div>
          {activeFilters > 0 && (
            <button
              onClick={clearAll}
              data-testid="btn-clear-filters"
              className="mt-4 text-xs text-slate-500 hover:text-[#DC2626] inline-flex items-center gap-1"
            >
              <X size={12} /> Limpar filtros
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="mt-6 hub-card overflow-hidden">
        <div className="overflow-x-auto">
          <table
            className="w-full text-sm min-w-[1200px]"
            data-testid="diagnostics-table"
          >
            <thead className="bg-[#F1F5F9] text-[#0F172A] uppercase text-[11px] tracking-[0.12em]">
              <tr>
                <Th>Empresa</Th>
                <Th>Responsável</Th>
                <Th>Cargo</Th>
                <Th>Cidade / UF</Th>
                <Th>Segmento</Th>
                <Th>Colab.</Th>
                <Th className="text-right">Índice</Th>
                <Th>Classificação</Th>
                <Th>Status Comercial</Th>
                <Th>Sessão</Th>
                <Th>Atualizado</Th>
                <Th>Data</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={12} className="p-8 text-center text-slate-500">
                    Carregando…
                  </td>
                </tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr>
                  <td colSpan={12} className="p-8 text-center text-slate-500">
                    Nenhum diagnóstico encontrado com os filtros aplicados.
                  </td>
                </tr>
              )}
              {!loading &&
                data.items.map((d) => {
                  const c = BADGE_COLOR(d.total_score || 0);
                  const cls = CLASSIFY(d.total_score || 0);
                  return (
                    <tr
                      key={d.id}
                      className="border-t border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <Td>
                        <Link
                          to={`/admin/diagnosticos/${d.id}`}
                          className="font-medium text-[#0F172A] hover:underline"
                          data-testid={`row-empresa-${d.id}`}
                        >
                          {d.company.empresa}
                        </Link>
                      </Td>
                      <Td>{d.company.nome}</Td>
                      <Td>{d.company.cargo}</Td>
                      <Td>
                        {d.company.cidade} / {d.company.estado}
                      </Td>
                      <Td>{d.company.segmento}</Td>
                      <Td>{d.company.colaboradores}</Td>
                      <Td className="text-right font-mono">
                        <span style={{ color: c, fontWeight: 600 }}>
                          {Math.round(d.total_score)}
                        </span>
                        <span className="text-slate-400">/100</span>
                      </Td>
                      <Td>
                        <span className="text-[11px] font-medium" style={{ color: c }}>
                          {cls}
                        </span>
                        <span className="text-slate-400"> · {d.badge}</span>
                      </Td>
                      <Td>
                        <span className="inline-flex text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                          {d.status_comercial || "Novo Diagnóstico"}
                        </span>
                      </Td>
                      <Td>
                        {d.sessao_estrategica ? (
                          <span className="text-xs font-medium text-[#16A34A]">
                            Sim
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">Não</span>
                        )}
                      </Td>
                      <Td>
                        {d.updated_at
                          ? new Date(d.updated_at).toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—"}
                      </Td>
                      <Td>
                        {new Date(d.created_at).toLocaleDateString("pt-BR")}
                      </Td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm">
          <div className="text-slate-500">
            {data.total} registro{data.total === 1 ? "" : "s"} · Página {page} de {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              data-testid="btn-prev-page"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="hub-btn-secondary disabled:opacity-50 py-2 px-4"
            >
              <ChevronLeft size={14} /> Anterior
            </button>
            <button
              data-testid="btn-next-page"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="hub-btn-secondary disabled:opacity-50 py-2 px-4"
            >
              Próximo <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
function Th({ children, className = "" }) {
  return (
    <th
      className={`px-4 py-3 text-left font-semibold whitespace-nowrap ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return (
    <td className={`px-4 py-3 whitespace-nowrap ${className}`}>{children}</td>
  );
}
